import { useTranslation } from 'react-i18next';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Text,
  Flex,
} from '@kognitos/lattice';
import type { IVersionHistoryItemData } from './types';

/**
 * Props for the RestoreAlertDialog component.
 */
export interface IRestoreAlertDialogProps {
  open: boolean;
  version: IVersionHistoryItemData | null;
  /** Whether viewing from a published automation context */
  fromPublished: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  isLoading?: boolean;
}

/**
 * Alert dialog for confirming version restore action.
 *
 * Displays a confirmation dialog explaining what will happen when restoring
 * a version, including what changes will be restored and that a new draft
 * will be created.
 *
 * @param props - Component props
 * @returns Restore alert dialog component
 *
 * @example
 * <RestoreAlertDialog
 *   open={isOpen}
 *   version={versionData}
 *   fromPublished={true}
 *   onOpenChange={setIsOpen}
 *   onConfirm={handleRestore}
 * />
 */
export function RestoreAlertDialog({
  open,
  version,
  fromPublished,
  onOpenChange,
  onConfirm,
  isLoading = false,
}: IRestoreAlertDialogProps) {
  const { t: tAutomation } = useTranslation('automation');
  const { t: tCommon } = useTranslation('common');

  const descriptionText = tAutomation(
    fromPublished
      ? 'automation.versionHistory.restore.publishedDescription'
      : 'automation.versionHistory.restore.draftDescription',
    {
      version: version?.version,
      timestamp: version?.title,
    },
  );

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="restore-alert-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>
            {tAutomation('automation.versionHistory.restore.title')}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <Flex direction="column" gap={4}>
              <Text level="small" color="muted">
                {descriptionText}
              </Text>
              <Text level="small" color="muted">
                {tAutomation(
                  'automation.versionHistory.restore.whatWillRestore',
                )}
              </Text>
              <Text level="small" color="muted">
                {tAutomation('automation.versionHistory.restore.newDraftInfo')}
              </Text>
            </Flex>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>
            {tCommon('common.action.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            disabled={isLoading}
            data-testid="restore-confirm-button"
          >
            {tCommon('common.action.restore')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
